import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { doc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import CommentsThread from '../common/CommentsThread';
import AssigneePicker from './AssigneePicker';
import ClientAutocomplete from './ClientAutocomplete';
import { openExternalUrl } from '../../utils/urlHelpers';
import {
  TASK_PRIORITIES,
  PRIORITY_COLORS,
  TASK_VISIBILITY
} from '../../utils/taskConstants';

const newSubtaskId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `st_${Math.random().toString(36).slice(2)}${performance.now().toString(36)}`;

// Asana-style pill toggle (custom button per PRD — not a MUI ToggleButton)
const PillToggle = ({ active, activeSx, icon, label, onClick, sx = {} }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.625,
      border: '1px solid rgba(0,0,0,0.35)',
      borderRadius: 4,
      bgcolor: '#fff',
      color: 'text.primary',
      fontSize: '0.8125rem',
      fontWeight: 500,
      transition: 'all 150ms',
      ...sx,
      ...(active ? activeSx : {})
    }}
  >
    {icon}
    {label}
  </ButtonBase>
);

/**
 * Task create/edit dialog (Tasks PRD §8).
 * - Completion toggles persist immediately (edit mode), independent of Save.
 * - Comments post immediately via CommentsThread; commentCount is maintained
 *   on the task doc so cards can show counts without extra listeners.
 * - Private tasks are always assigned to their creator — enforced again in
 *   the save payload regardless of UI state.
 */
const TaskDialog = ({
  open,
  task, // null in create mode
  defaultColumnKey,
  activeUsers,
  usersByUid,
  activeClients,
  currentUser,
  onClose,
  onSave,
  onDelete,
  saving
}) => {
  const isNew = !task;
  const [form, setForm] = useState(null);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      title: task?.title || '',
      description: task?.description || '',
      assigneeUid: task?.assigneeUid || null,
      priority: task?.priority || 'Medium',
      dueDate: task?.dueDate || '',
      clientId: task?.clientId || null,
      visibility: task?.visibility || TASK_VISIBILITY.COMPANY,
      urls: task?.urls ? [...task.urls] : [],
      subtasks: task?.subtasks ? task.subtasks.map((s) => ({ ...s })) : [],
      newSubtask: ''
    });
    setCompleted(Boolean(task?.completed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  if (!form) return null;

  const isPrivate = form.visibility === TASK_VISIBILITY.PRIVATE;
  const isCreator = isNew || task.creatorUid === currentUser.uid;

  const setField = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleToggleVisibility = () => {
    if (isPrivate) {
      // Back to company: assignee stays as-is, picker unlocks
      setField({ visibility: TASK_VISIBILITY.COMPANY });
    } else {
      // Private tasks are always assigned to their creator
      const snapUid = isNew ? currentUser.uid : task.creatorUid;
      setField({ visibility: TASK_VISIBILITY.PRIVATE, assigneeUid: snapUid });
    }
  };

  const handleToggleComplete = async () => {
    if (isNew) return;
    const next = !completed;
    setCompleted(next);
    try {
      await updateDoc(doc(db, 'tasks', task.id), {
        completed: next,
        completedAt: next ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error toggling completion:', error);
      setCompleted(!next);
    }
  };

  const bumpCommentCount = (delta) => {
    if (isNew) return;
    updateDoc(doc(db, 'tasks', task.id), { commentCount: increment(delta) }).catch((error) =>
      console.error('Error updating comment count:', error)
    );
  };

  const handleSave = () => {
    if (!form.title.trim()) return;
    const payload = {
      title: form.title.trim(),
      description: form.description,
      priority: form.priority,
      dueDate: form.dueDate || null,
      clientId: form.clientId || null,
      visibility: form.visibility,
      assigneeUid:
        form.visibility === TASK_VISIBILITY.PRIVATE
          ? (isNew ? currentUser.uid : task.creatorUid)
          : form.assigneeUid || null,
      urls: form.urls.map((u) => u.trim()).filter(Boolean),
      subtasks: form.subtasks.map(({ id, text, done }) => ({ id, text, done }))
    };
    onSave(payload, task);
  };

  const addSubtask = () => {
    const text = form.newSubtask.trim();
    if (!text) return;
    setField({
      subtasks: [...form.subtasks, { id: newSubtaskId(), text, done: false }],
      newSubtask: ''
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Title bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, pt: 2.5, pb: 2 }}>
        {!isNew && (
          <PillToggle
            active={completed}
            onClick={handleToggleComplete}
            icon={<CheckIcon sx={{ fontSize: 14, color: completed ? '#fff' : 'text.secondary' }} />}
            label={completed ? 'Completed' : 'Mark complete'}
            sx={{
              '&:hover': { borderColor: '#2e7d32', bgcolor: 'rgba(46,125,50,0.04)' }
            }}
            activeSx={{
              bgcolor: '#2e7d32',
              borderColor: '#2e7d32',
              color: '#fff',
              '&:hover': { bgcolor: '#1b5e20', borderColor: '#1b5e20' }
            }}
          />
        )}
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: PRIORITY_COLORS[form.priority] || PRIORITY_COLORS.Medium,
            flexShrink: 0
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {isNew ? 'New Task' : 'Edit Task'}
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <PillToggle
            active={isPrivate}
            onClick={handleToggleVisibility}
            icon={
              isPrivate ? (
                <LockIcon sx={{ fontSize: 14, color: 'text.primary' }} />
              ) : (
                <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              )
            }
            label={isPrivate ? 'Private' : 'Company'}
            sx={{ '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' } }}
            activeSx={{ bgcolor: 'rgba(0,0,0,0.06)', borderColor: 'text.primary' }}
          />
        </Box>
      </Box>

      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 3.5, minHeight: 520 }}>
          {/* LEFT: form */}
          <Box sx={{ flex: 1, minWidth: 0, pt: 1 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Title"
                  value={form.title}
                  onChange={(e) => setField({ title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={6}
                  value={form.description}
                  onChange={(e) => setField({ description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Assignee
                </Typography>
                <AssigneePicker
                  users={activeUsers}
                  usersByUid={usersByUid}
                  value={form.assigneeUid}
                  onChange={(uid) => setField({ assigneeUid: uid })}
                  locked={isPrivate}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Priority *
                </Typography>
                <FormControl fullWidth size="medium">
                  <Select
                    value={form.priority}
                    onChange={(e) => setField({ priority: e.target.value })}
                    sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
                  >
                    {TASK_PRIORITIES.map((p) => (
                      <MenuItem key={p} value={p} sx={{ gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: PRIORITY_COLORS[p]
                          }}
                        />
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setField({ dueDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ClientAutocomplete
                  clients={activeClients}
                  value={form.clientId}
                  onChange={(id) => setField({ clientId: id })}
                  includeNoneOption={false}
                  label="Client"
                  placeholder="No client"
                  size="medium"
                />
              </Grid>

              {/* URLs */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  URLs
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {form.urls.map((url, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="https://..."
                        value={url}
                        onChange={(e) => {
                          const urls = [...form.urls];
                          urls[i] = e.target.value;
                          setField({ urls });
                        }}
                      />
                      <Tooltip title="Open in new tab">
                        <IconButton
                          size="small"
                          onClick={() => openExternalUrl(url)}
                          sx={{ visibility: url.trim() ? 'visible' : 'hidden', color: 'primary.main' }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => setField({ urls: form.urls.filter((_, j) => j !== i) })}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setField({ urls: [...form.urls, ''] })}
                    sx={{ alignSelf: 'flex-start', fontSize: '0.8125rem' }}
                  >
                    Add URL
                  </Button>
                </Box>
              </Grid>

              {/* Subtasks */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Subtasks
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {form.subtasks.map((subtask) => (
                    <Box
                      key={subtask.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 0.5,
                        py: 0.75,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                        '&:hover .subtask-delete': { opacity: 1 }
                      }}
                    >
                      <Box
                        onClick={() =>
                          setField({
                            subtasks: form.subtasks.map((s) =>
                              s.id === subtask.id ? { ...s, done: !s.done } : s
                            )
                          })
                        }
                        sx={{
                          width: 18,
                          height: 18,
                          border: subtask.done ? 'none' : '1.5px solid rgba(0,0,0,0.4)',
                          borderRadius: 0.75,
                          bgcolor: subtask.done ? 'primary.main' : 'transparent',
                          color: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                          '&:hover': { borderColor: 'primary.main' }
                        }}
                      >
                        {subtask.done && <CheckIcon sx={{ fontSize: 14 }} />}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          color: subtask.done ? 'text.secondary' : 'text.primary',
                          textDecoration: subtask.done ? 'line-through' : 'none'
                        }}
                      >
                        {subtask.text}
                      </Typography>
                      <IconButton
                        className="subtask-delete"
                        size="small"
                        onClick={() =>
                          setField({ subtasks: form.subtasks.filter((s) => s.id !== subtask.id) })
                        }
                        sx={{
                          opacity: 0,
                          transition: 'opacity 120ms',
                          color: 'rgba(0,0,0,0.25)',
                          '&:hover': { color: 'error.main', bgcolor: 'rgba(211,47,47,0.08)' }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.75 }}>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        border: '1.5px dashed rgba(0,0,0,0.3)',
                        borderRadius: 0.75,
                        flexShrink: 0
                      }}
                    />
                    <TextField
                      fullWidth
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      placeholder="Add a subtask — press Enter"
                      value={form.newSubtask}
                      onChange={(e) => setField({ newSubtask: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addSubtask();
                        }
                      }}
                      sx={{ '& input': { fontSize: '0.875rem' } }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* RIGHT: comments */}
          <Divider orientation="vertical" flexItem />
          <Box sx={{ width: '38%', flexShrink: 0, display: 'flex', flexDirection: 'column', pt: 1 }}>
            {isNew ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
                Save the task to start commenting.
              </Typography>
            ) : (
              <CommentsThread
                pathSegments={['tasks', task.id, 'comments']}
                currentUser={currentUser}
                usersByUid={usersByUid}
                variant="task"
                onAfterPost={() => bumpCommentCount(1)}
                onAfterDelete={() => bumpCommentCount(-1)}
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Bottom actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, pb: 2.5, pt: 1.5 }}>
        <Box>
          {!isNew && isCreator && (
            <Button color="error" onClick={() => onDelete(task)}>
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.title.trim()}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TaskDialog;
